import type { V2 } from '../core/math';
import type { Shape } from './types';

export interface RayShapeHit {
  t: number;
  nx: number;
  nz: number;
}

/**
 * Ray (origin o, unit direction d) vs shape at (pos, angle). Returns the entry hit within
 * [0, maxT], or null. Rays starting inside a shape report t = 0 with normal = -d.
 */
export function rayShape(o: V2, d: V2, maxT: number, shape: Shape, pos: V2, angle: number, out: RayShapeHit): boolean {
  if (shape.type === 'circle') return rayCircle(o, d, maxT, pos, shape.r, out);
  // Transform ray into box-local space (inverse rotation).
  const c = Math.cos(-angle);
  const s = Math.sin(-angle);
  const ox = o.x - pos.x;
  const oz = o.z - pos.z;
  const lox = ox * c + oz * s;
  const loz = -ox * s + oz * c;
  const ldx = d.x * c + d.z * s;
  const ldz = -d.x * s + d.z * c;
  let tmin = 0;
  let tmax = maxT;
  let nAxis = -1; // 0 = x, 1 = z
  let nSign = 0;
  // X slab
  if (Math.abs(ldx) < 1e-12) {
    if (lox < -shape.hx || lox > shape.hx) return false;
  } else {
    const inv = 1 / ldx;
    let t1 = (-shape.hx - lox) * inv;
    let t2 = (shape.hx - lox) * inv;
    let sgn = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      sgn = 1;
    }
    if (t1 > tmin) {
      tmin = t1;
      nAxis = 0;
      nSign = sgn;
    }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  // Z slab
  if (Math.abs(ldz) < 1e-12) {
    if (loz < -shape.hz || loz > shape.hz) return false;
  } else {
    const inv = 1 / ldz;
    let t1 = (-shape.hz - loz) * inv;
    let t2 = (shape.hz - loz) * inv;
    let sgn = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      sgn = 1;
    }
    if (t1 > tmin) {
      tmin = t1;
      nAxis = 1;
      nSign = sgn;
    }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  out.t = tmin;
  if (nAxis < 0) {
    // Started inside.
    out.nx = -d.x;
    out.nz = -d.z;
    return true;
  }
  // Local normal -> world (rotate by +angle).
  const lnx = nAxis === 0 ? nSign : 0;
  const lnz = nAxis === 1 ? nSign : 0;
  const cw = Math.cos(angle);
  const sw = Math.sin(angle);
  out.nx = lnx * cw + lnz * sw;
  out.nz = -lnx * sw + lnz * cw;
  return true;
}

function rayCircle(o: V2, d: V2, maxT: number, c: V2, r: number, out: RayShapeHit): boolean {
  const mx = o.x - c.x;
  const mz = o.z - c.z;
  const b = mx * d.x + mz * d.z;
  const cc = mx * mx + mz * mz - r * r;
  if (cc <= 0) {
    out.t = 0;
    out.nx = -d.x;
    out.nz = -d.z;
    return true;
  }
  if (b > 0) return false;
  const disc = b * b - cc;
  if (disc < 0) return false;
  const t = -b - Math.sqrt(disc);
  if (t < 0 || t > maxT) return false;
  out.t = t;
  const px = o.x + d.x * t - c.x;
  const pz = o.z + d.z * t - c.z;
  const l = Math.sqrt(px * px + pz * pz) || 1;
  out.nx = px / l;
  out.nz = pz / l;
  return true;
}
